import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Globe, 
  Zap, 
  Shield, 
  Smartphone, 
  Search, 
  Code,
  Database,
  Cloud,
  Blocks,
  FileCode,
  Settings,
  Bot,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import i18n from '../../i18n';

const WebDevelopment = () => {
  const { t, ready } = useTranslation();

  // Force reload translations when component mounts
  useEffect(() => {
    console.log('WebDevelopment component mounted, reloading translations');
    i18n.reloadResources().then(() => {
      console.log('Translations successfully reloaded in WebDevelopment');
      // Debug translation values
      console.log('Current language:', i18n.language);
      console.log('WebDevelopment title translation:', t('services.webDevelopment.title'));
      console.log('Description translation:', t('services.webDevelopment.description'));
      console.log('Features title translation:', t('services.webDevelopment.featuresTitle'));
      
      // Debugging more information
      console.log('DEBUG - All translation values:');
      console.log('services object:', i18n.t('services', { returnObjects: true }));
      console.log('services.webDevelopment object:', i18n.t('services.webDevelopment', { returnObjects: true }));
    }).catch(err => {
      console.error('Failed to reload translations in WebDevelopment:', err);
    });
  }, [t]);

  const features = [
    {
      icon: Globe,
      title: t('services.webDevelopment.features.responsive.title'),
      description: t('services.webDevelopment.features.responsive.description')
    },
    {
      icon: Search,
      title: t('services.webDevelopment.features.seo.title'),
      description: t('services.webDevelopment.features.seo.description')
    },
    {
      icon: Zap,
      title: t('services.webDevelopment.features.fast.title'),
      description: t('services.webDevelopment.features.fast.description')
    },
    {
      icon: Shield,
      title: t('services.webDevelopment.features.secure.title'),
      description: t('services.webDevelopment.features.secure.description')
    },
    {
      icon: Settings,
      title: t('services.webDevelopment.features.custom.title'),
      description: t('services.webDevelopment.features.custom.description')
    },
    {
      icon: Cloud,
      title: t('services.webDevelopment.features.support.title'),
      description: t('services.webDevelopment.features.support.description')
    }
  ];

  const platforms = [
    {
      icon: Code,
      title: t('services.webDevelopment.platforms.openSolutions.title'),
      description: t('services.webDevelopment.platforms.openSolutions.description'),
      features: [
        "Next.js og React",
        "Vue.js og Nuxt",
        "Node.js bakendi",
        "TypeScript",
        "GraphQL",
        "REST API",
        "Microservices arkitektúr"
      ]
    },
    {
      icon: Blocks,
      title: t('services.webDevelopment.platforms.headlessCms.title'),
      description: t('services.webDevelopment.platforms.headlessCms.description'),
      features: [
        "Prismic CMS",
        "Strapi",
        "Contentful",
        "Sanity",
        "GraphCMS",
        "DatoCMS",
        "Sérsniðin CMS lausnir"
      ]
    },
    {
      icon: Database,
      title: t('services.webDevelopment.platforms.databases.title'),
      description: t('services.webDevelopment.platforms.databases.description'),
      features: [
        "PostgreSQL",
        "MySQL",
        "MongoDB",
        "Redis",
        "Supabase",
        "Firebase",
        "ElasticSearch"
      ]
    }
  ];

  // If translations are not ready, show loading state
  if (!ready) {
    return <div className="min-h-screen flex items-center justify-center">Hleður...</div>;
  }

  return (
    <>
      <SEO 
        title={t('services.webDevelopment.title')}
        description={t('services.webDevelopment.description')}
        keywords={t('site.keywords')}
      />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <motion.div 
              className="text-center max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                {t('services.webDevelopment.title')}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {t('services.webDevelopment.description')}
              </p>
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                  >
                    {t('services.webDevelopment.cta.button')}
                    <ArrowRight className="ml-2" />
                  </motion.button>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Features Section */}
        <motion.div 
          className="py-24 bg-white"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('services.webDevelopment.featuresTitle')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('services.webDevelopment.featuresDescription')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div 
                  key={index}
                  className="bg-gray-50 rounded-xl p-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                    <feature.icon className="w-6 h-6 text-[#EA295A]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Platforms Section */}
        <motion.div 
          className="py-24 bg-gray-50"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('services.webDevelopment.platforms.title')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('services.webDevelopment.platforms.description')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {platforms.map((platform, index) => (
                <motion.div 
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                    <platform.icon className="w-6 h-6 text-[#EA295A]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{platform.title}</h3>
                  <p className="text-gray-600 mb-6">{platform.description}</p>
                  <ul className="space-y-3">
                    {Array.isArray(platform.features) && platform.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center text-gray-600">
                        <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          className="py-24 bg-white"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('services.webDevelopment.cta.title')}
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                {t('services.webDevelopment.cta.description')}
              </p>
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center mx-auto"
                >
                  {t('services.webDevelopment.cta.button')}
                  <ArrowRight className="ml-2" />
                </motion.button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default WebDevelopment;